import { FC, useMemo } from 'react';
import { Maximize } from '@carbon/react/icons';
import { useAtomValue } from 'jotai';
import { CrossPlot } from 'components/charts/cross-plot';
import { ExpandableContainer } from 'components/expandable-container';
import { useCrossPlotData } from '../hooks';
import type { FilteredWell, CrossPlotChartConfig } from '../types';
import {
  tcCrossPlotChartsAtom,
  tcWellsLoadingAtom,
} from '../type-curve-filters/atoms';
import styles from './type-curve-volume-charts.module.scss';

interface TypeCurveCrossPlotProps {
  wells: FilteredWell[];
  typeCurveHeaderDetailsId: number | null;
  dcaScenarioId: number;
  lateralLengthFt: number | null;
  typeCurveDateCreated: string | null;
  structureGridCollectionId?: number | null;
  /** UWI to highlight (controlled by the parent section). */
  highlightedUwi?: string | null;
  /** Fired when the user hovers over / leaves a point (lifted to parent). */
  onPointHover?: (uwi: string | null) => void;
  /** UWI → hex color map for color-by-attribute. */
  uwiColorMap?: Record<string, string>;
  proppantIntensityLbsFt: number | null;
  fluidIntensityBblFt: number | null;
  fluidIntensityGalFt: number | null;
  horizontalWellSpacingFt: number | null;
}

// ---------------------------------------------------------------------------
// Per-chart card — each user-configured chart owns its own `useCrossPlotData`
// call so they fetch independently and can mount / unmount without affecting
// siblings (matches the per-stream pattern used in TypeCurveVolumeCharts /
// TypeCurveProbitPlots).
// ---------------------------------------------------------------------------
interface CrossPlotChartCardProps {
  chart: CrossPlotChartConfig;
  wells: FilteredWell[];
  typeCurveHeaderDetailsId: number | null;
  dcaScenarioId: number;
  lateralLengthFt: number;
  typeCurveDateCreated: string;
  structureGridCollectionId?: number | null;
  highlightedUwi?: string | null;
  onPointHover?: (uwi: string | null) => void;
  uwiColorMap?: Record<string, string>;
  wellsLoading: boolean;
  proppantIntensityLbsFt: number | null;
  fluidIntensityBblFt: number | null;
  fluidIntensityGalFt: number | null;
  horizontalWellSpacingFt: number | null;
}

const CrossPlotChartCard: FC<CrossPlotChartCardProps> = ({
  chart,
  wells,
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  lateralLengthFt,
  typeCurveDateCreated,
  structureGridCollectionId,
  highlightedUwi,
  onPointHover,
  uwiColorMap,
  wellsLoading,
  proppantIntensityLbsFt,
  fluidIntensityBblFt,
  fluidIntensityGalFt,
  horizontalWellSpacingFt,
}) => {
  const tcHeaderValues = useMemo(
    () => ({
      lateralLengthFt,
      proppantIntensityLbsFt,
      fluidIntensityBblFt,
      fluidIntensityGalFt,
      horizontalWellSpacingFt,
    }),
    [
      lateralLengthFt,
      proppantIntensityLbsFt,
      fluidIntensityBblFt,
      fluidIntensityGalFt,
      horizontalWellSpacingFt,
    ]
  );

  const {
    data,
    isLoading: dataLoading,
    error,
  } = useCrossPlotData({
    filteredUwis: wells,
    typeCurveHeaderDetailsId,
    dcaScenarioId,
    xAxis: chart.xAxis,
    yAxis: chart.yAxis,
    tcLateralLengthFt: lateralLengthFt,
    typeCurveDateCreated,
    structureGridCollectionId,
    tcHeaderValues,
  });

  return (
    <ExpandableContainer>
      {({ isExpanded, expand }) => (
        <CrossPlot
          id={chart.id}
          data={data}
          isLoading={dataLoading || wellsLoading}
          error={error}
          uwiColorMap={uwiColorMap}
          highlightedUwi={highlightedUwi}
          onPointHover={onPointHover}
          exportConfig={
            // No export trigger in the expanded view — it would collide with
            // the expand overlay's close button.
            isExpanded
              ? undefined
              : {
                  label: chart.title,
                  filenamePrefix: `type-curve-cross-plot-${chart.id}`,
                }
          }
          header={
            <div className={styles.chartTitleRow}>
              <span className={styles.chartTitle}>{chart.title}</span>
              <div className={styles.chartHeaderActions}>
                {!isExpanded && (
                  <button
                    type="button"
                    className={styles.chartExpandButton}
                    onClick={expand}
                    aria-label={`Expand ${chart.title}`}
                  >
                    <Maximize size={16} />
                  </button>
                )}
              </div>
            </div>
          }
        />
      )}
    </ExpandableContainer>
  );
};

export const TypeCurveCrossPlot: FC<TypeCurveCrossPlotProps> = ({
  wells,
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  lateralLengthFt,
  typeCurveDateCreated,
  structureGridCollectionId,
  highlightedUwi,
  onPointHover,
  uwiColorMap,
  proppantIntensityLbsFt,
  fluidIntensityBblFt,
  fluidIntensityGalFt,
  horizontalWellSpacingFt,
}) => {
  const charts = useAtomValue(tcCrossPlotChartsAtom);
  const wellsLoading = useAtomValue(tcWellsLoadingAtom);

  // Bail out early if the TC isn't ready — the cross-plot API needs both the
  // lateral length and the TC date-created to scale the X/Y values correctly.
  if (!lateralLengthFt || !typeCurveDateCreated) return null;

  const visibleCharts = charts.filter((c) => c.visible);
  if (visibleCharts.length === 0) return null;

  return (
    <>
      {visibleCharts.map((chart) => (
        <CrossPlotChartCard
          key={chart.id}
          chart={chart}
          wells={wells}
          typeCurveHeaderDetailsId={typeCurveHeaderDetailsId}
          dcaScenarioId={dcaScenarioId}
          lateralLengthFt={lateralLengthFt}
          typeCurveDateCreated={typeCurveDateCreated}
          structureGridCollectionId={structureGridCollectionId}
          highlightedUwi={highlightedUwi}
          onPointHover={onPointHover}
          uwiColorMap={uwiColorMap}
          wellsLoading={wellsLoading}
          proppantIntensityLbsFt={proppantIntensityLbsFt}
          fluidIntensityBblFt={fluidIntensityBblFt}
          fluidIntensityGalFt={fluidIntensityGalFt}
          horizontalWellSpacingFt={horizontalWellSpacingFt}
        />
      ))}
    </>
  );
};
